import { useSelector } from "react-redux";
import React, { useContext, useMemo, useState } from "react";
import {
  getCoinCardKey,
  getCoinsOrContractsData,
} from "@shared/features/portfolio/utils";
import {
  ConsolidatedCoinData,
  ConsolidatedCoinDataWithKey,
  LanguageNameSpaces,
  ProcessedCoinData,
  SortingOrder,
  WorkspaceType,
} from "@shared/types";
import { checkSymbolSearch } from "@shared/utils/misc";
import { selectPreprocessedPortfolioData } from "@shared/slices/portfolio.slice.ts";
import {
  COIN_CARD_COLUMN_SPANS,
  POSITION_SORT_TO_PATH,
} from "features/portfolio/common/constants";
import { RootState } from "app/store.ts";
import { ColumnType } from "@shared/common/CardsList/types";
import {
  getSortedDataBySortSelect,
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general.ts";
import useCardSorters from "@shared/common/CardSorters/useCardSorters";
import CardSorters from "common/CardSorters/CardSorters";
import { PositionsSort } from "@shared/features/portfolio/types";
import { PositionsContext } from "features/portfolio/Positions/Positions.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useHistory } from "react-router-dom";
import { CREATE_ACCOUNT_PATH, IS_WHOP } from "common/constants.ts";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import WhopCreateAccountModal from "features/whop/WhopCreateAccountModal.tsx";
import { checkPositionSearch } from "features/portfolio/common/positionSearch";

interface UseAssetsProps {
  dataSelector: (state: RootState) => ConsolidatedCoinData[];
  searchFilter?: string;
  isPositions?: boolean;
}

export const useAssetsData = ({
  dataSelector,
  searchFilter,
  isPositions,
}: UseAssetsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const longDirection = t("positions.long");
  const shortDirection = t("positions.short");
  const { sortBy, sortDirection } = useContext(PositionsContext) || {};
  const data = useSelector(dataSelector);
  const portfolioData = useSelector(selectPreprocessedPortfolioData);
  const rawPortfolioData = useMemo(() => {
    return getCoinsOrContractsData<ProcessedCoinData>(
      portfolioData,
      isPositions
    );
  }, [portfolioData, isPositions]);

  const filteredCoinsData = useMemo(() => {
    const rawData = getCoinsOrContractsData<ConsolidatedCoinData>(
      data,
      isPositions
    );
    return rawData
      .filter((coinData) =>
        isPositions
          ? checkPositionSearch(
              coinData,
              searchFilter,
              coinData.is_short ? shortDirection : longDirection
            )
          : checkSymbolSearch(coinData.symbol, searchFilter)
      )
      .map((coinData) => {
        return {
          ...coinData,
          key: getCoinCardKey(coinData),
        };
      });
  }, [data, searchFilter, isPositions, longDirection, shortDirection]);

  const topSymbol = useMemo(() => {
    return filteredCoinsData
      .slice()
      .sort((a, b) => (b.margin?.[2] || 0) - (a.margin?.[2] || 0))?.[0];
  }, [filteredCoinsData]);

  const sortedCoinsData = useMemo(() => {
    if (!sortBy || !sortDirection) return filteredCoinsData;
    return getSortedDataBySortSelect<
      ConsolidatedCoinDataWithKey,
      PositionsSort
    >(filteredCoinsData || [], sortBy, sortDirection, POSITION_SORT_TO_PATH, [
      PositionsSort.SYMBOL,
    ]);
  }, [filteredCoinsData, sortBy, sortDirection]);

  return {
    rawPortfolioData,
    filteredCoinsData,
    sortedCoinsData,
    topSymbol,
  };
};

interface UseAssetsCardsAndSortersProps {
  data: ConsolidatedCoinDataWithKey[];
}

export const useAssetsCardsAndSorters = ({
  data,
}: UseAssetsCardsAndSortersProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);

  const columns: ColumnType<ConsolidatedCoinDataWithKey>[] = [
    {
      title: t("assets.coin"),
      span: COIN_CARD_COLUMN_SPANS[0],
      key: "coin",
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
    },
    {
      title: t("assets.amount"),
      span: COIN_CARD_COLUMN_SPANS[1],
      key: "amount",
      sorter: (a, b) => sortByNumberProperty(a, b, "total_in_usd"),
      defaultSortOrder: SortingOrder.DESCEND,
    },
    {
      title: t("assets.price"),
      span: COIN_CARD_COLUMN_SPANS[2],
      key: "price",
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change[0]"),
    },
  ];

  const keys = useMemo(() => data.map((item) => item.key), [data]);

  const { sortedData, cardsSorters } = useCardSorters({
    CardSorters,
    columns,
    keys,
    data,
  });

  return {
    sortedData,
    cardsSorters,
  };
};

export const useCreateAccountClick = (primaryExchange?: string) => {
  const history = useHistory();
  const workspace = useSelector(selectWorkspace);
  const [whopModalOpen, setWhopModalOpen] = useState(false);

  const onCreateAccountClick = (
    eventOrAllowDemo?: React.MouseEvent | boolean
  ) => {
    if (
      IS_WHOP &&
      ((typeof eventOrAllowDemo === "boolean" && eventOrAllowDemo) ||
        workspace !== WorkspaceType.DEMO)
    ) {
      setWhopModalOpen(true);
    } else {
      history.push(CREATE_ACCOUNT_PATH);
    }
  };

  return {
    onCreateAccountClick,
    whopModal: IS_WHOP ? (
      <WhopCreateAccountModal
        open={whopModalOpen}
        onClose={() => setWhopModalOpen(false)}
        primaryExchange={primaryExchange}
      />
    ) : undefined,
  };
};
